'use strict';

/**
 * shared/graph/monitoring-state.js — THE ONE canonical `monitoringState` vocabulary + normalizer.
 *
 * WHY THIS EXISTS (entity-monitoringState leg, MS0)
 *   `tags.monitoringState` is the entity-graph LIFECYCLE axis — "are we observing this entity
 *   at all?" — widened this leg from the half-built `active | dormant` to `live | dormant |
 *   archived`. Because the legacy stored value is `'active'` and the field is frequently ABSENT
 *   on older docs, every reader must funnel the raw stored value through ONE normalizer instead
 *   of hand-checking string literals (drift + the silent-revival footgun below). Both runtimes
 *   consume it: the Node/CJS backend services (MS2 readers) via relative require, and the
 *   Next.js TS layer (MS-UI + `src/types/tags.ts` re-export) via the `@shared/*` alias + the
 *   `.d.ts` sidecar — exactly the cross-runtime pattern of `shared/publish/transitions.js`,
 *   `shared/graph/confidence.js`, and `shared/knowledge/entity-gaps.js`.
 *
 * PURITY CONTRACT (cloned from shared/graph/confidence.js)
 *   Requires NOTHING (no firebase, no env, no clock). Pure functions of their inputs —
 *   referentially transparent, trivially importable from a browser bundle, and safe to classify
 *   as a pure test target in `backend/scripts/run-gates.js`.
 *
 * ⚠⚠ THE INVARIANT RULE — filter `!= 'archived'`, NEVER `== 'live'`.
 *   `absent` (and legacy `'active'`) MUST count as LIVE. A Firestore query
 *   `where('monitoringState','==','live')` silently matches ZERO legacy/absent docs (the MCP
 *   boolean-filter-silent-empty hazard), and an in-memory `state === 'live'` gate silently
 *   EXCLUDES every doc that predates this leg. The honest test for "should we suspend?" is
 *   `normalizeMonitoringState(raw) === 'archived'` (or `=== 'dormant'`); the honest test for
 *   "is it still live?" is `normalizeMonitoringState(raw) !== 'archived'`. Never gate on
 *   equality-to-`'live'`.
 *
 * FAIL-SAFE FOR UNKNOWNS
 *   An unrecognized/absent value normalizes to `'live'`, NOT `'archived'`. This is deliberate:
 *   the failure mode of "treat it as live" is a bit of extra spend on a maybe-dead entity; the
 *   failure mode of "treat it as archived" is FREEZING a real live entity's page + hiding it
 *   from agents. Only an EXPLICIT `'archived'`/`'dormant'` suspends. Consistent with the
 *   invariant rule (absent ⇒ live).
 */

/** The canonical lifecycle vocabulary (widened from the legacy `active | dormant`). */
const MONITORING_STATES = Object.freeze(['live', 'dormant', 'archived']);

/**
 * Normalize any stored `monitoringState` (widened vocab, legacy `'active'`, absent, or garbage)
 * to one of `MONITORING_STATES`. Migration is normalizer-on-read, not a data backfill:
 *   - absent / null / '' / legacy `'active'` / any unknown value → `'live'` (fail-safe).
 *   - `'live'` / `'dormant'` / `'archived'` → passthrough.
 * Route EVERY read through this (mirrors `normalizeEntityPriority`) so the widening is
 * zero-downtime and no reader hand-checks string literals.
 *
 * @param {unknown} raw the raw stored value (`tags.monitoringState`)
 * @returns {'live' | 'dormant' | 'archived'}
 */
function normalizeMonitoringState(raw) {
  if (raw === 'dormant' || raw === 'archived' || raw === 'live') return raw;
  // absent / null / '' / legacy 'active' / anything unrecognized → live (fail-safe; see header).
  return 'live';
}

/**
 * The ONE "should the auto-flywheel suspend on this entity?" predicate (MS2). True iff the
 * normalized state is NOT live — i.e. `'dormant'` OR `'archived'`. `archived` is STRONGER than
 * `dormant` (terminal wind-down vs reversible cost-pause), but both suspend the same auto-mutating
 * readers (revival gate, drafting, trajectory recompute, open-questions, legibility eligibility),
 * so the four dormant-era skips generalize to this single call. Routed through the normalizer, so
 * absent / legacy `'active'` correctly read as live → NOT suspended (the invariant; never a
 * silent-revival footgun). This is the blessed idiom for the suspend decision — do NOT hand-check
 * `=== 'dormant'` (that would miss archived) and NEVER gate on `=== 'live'`.
 *
 * @param {unknown} raw the raw stored value (`tags.monitoringState`)
 * @returns {boolean} true when drafting/ingest/recompute must SKIP this entity
 */
function isMonitoringSuspended(raw) {
  return normalizeMonitoringState(raw) !== 'live';
}

/**
 * The "is this entity FROZEN (terminal wind-down)?" predicate — true iff the normalized state is
 * EXACTLY `'archived'`. This is the STRONGER, archived-ONLY sibling of `isMonitoringSuspended`:
 * where `isMonitoringSuspended` suspends the auto-flywheel on `dormant` OR `archived` (the
 * cost-governor), some surfaces must hard-skip ONLY the terminal `archived` state while
 * DELIBERATELY keeping `dormant` (the reversible cost-pause) included — e.g. narrative surfaces
 * where a dormant entity's residual activity is legitimately narrated but a wound-down (archived)
 * entity must never be revived (STORY-RECAP-ARCHIVED: story membership must not open/join a story
 * for an archived subject; the ecosystem recap must not gather archived entities). Use THIS (not
 * `isMonitoringSuspended`) exactly when dormant must stay live-equivalent. Routed through the
 * normalizer, so absent / legacy `'active'` read as live → NOT archived (the invariant; never gate
 * on `=== 'live'`). Do NOT hand-check `raw === 'archived'` (a raw check misses nothing here, but
 * funneling through the normalizer keeps the ONE-vocabulary contract and matches the sibling).
 *
 * @param {unknown} raw the raw stored value (`tags.monitoringState`)
 * @returns {boolean} true ONLY when the entity is terminally archived (frozen)
 */
function isMonitoringArchived(raw) {
  return normalizeMonitoringState(raw) === 'archived';
}

module.exports = {
  MONITORING_STATES,
  normalizeMonitoringState,
  isMonitoringSuspended,
  isMonitoringArchived,
};
